import Vue from '@/vue'
import * as Sentry from '@sentry/vue'
import type { CustomWindow } from '@/misc/custom-window'

declare let window: CustomWindow

const env: string = typeof APP_ENV === 'undefined' ? import.meta.env.VITE_APP_ENV : APP_ENV

Sentry.init({
  Vue: Vue,
  tracePropagationTargets: ['localhost', typeof APP_URL === 'undefined' ? import.meta.env.VITE_APP_URL : APP_URL, /^\//],
  dsn: typeof SENTRY_LARAVEL_DSN === 'undefined' ? import.meta.env.VITE_SENTRY_LARAVEL_DSN : SENTRY_LARAVEL_DSN,
  environment: env,
  release: `mertis@${typeof BITBUCKET_BUILD_NUMBER === 'undefined' ? import.meta.env.VITE_BITBUCKET_BUILD_NUMBER : BITBUCKET_BUILD_NUMBER}`,
  dist: typeof BITBUCKET_BUILD_NUMBER === 'undefined' ? import.meta.env.VITE_BITBUCKET_BUILD_NUMBER : BITBUCKET_BUILD_NUMBER,
  initialScope: {
    tags: { 'tenant': window.TENANT_UUID },
    user: window.SENTRY_USER_SCOPE,
  },
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  tracesSampleRate: 0.07,
  maxValueLength: 300,
  normalizeDepth: 9,
  integrations: [
    Sentry.vueIntegration({
      tracingOptions: {
        trackComponents: true,
      },
    }),
    Sentry.browserTracingIntegration(),
    Sentry.extraErrorDataIntegration({
      // Limit of how deep the object serializer should go. Anything deeper than limit will
      // be replaced with standard Node.js REPL notation of [Object], [Array], [Function] or
      // a primitive value. Defaults to 3.
      // When changing this value, make sure to update `normalizeDepth` of the whole SDK
      // to `depth + 1` in order to get it serialized properly - https://docs.sentry.io/platforms/javascript/configuration/options/#normalize-depth
      depth: 8
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
    })
  ],
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: env === 'production' ? 0.0 : 0.5,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: env === 'production' ? 0.2 : 0.5,
  ignoreErrors: [
    'Network Error',
    'Non-Error exception captured',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'Request failed with status code 401',
    'Request failed with status code 404',
    'Request failed with status code 429',
    'Request failed with status code 422',
  ],
})

Sentry.setUser(window.SENTRY_USER_SCOPE)

export default Sentry
